import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShell } from '../../shell-context.js';
import { NewPurchaseOrderDialog } from './NewPurchaseOrderDialog.js';
import {
  grnPath,
  PURCHASE_ORDERS_PATH,
  type PurchaseOrderRoute,
  purchaseOrderPath,
} from './orders.js';
import { PurchaseOrderDetailView } from './PurchaseOrderDetailView.js';
import { PurchaseOrderList } from './PurchaseOrderList.js';
import { requisitionPath } from './requisitions.js';
import { rfqPath } from './rfqs.js';
import type { PurchaseOrderSource } from './source.js';

export interface PurchaseOrdersScreenProps {
  source: PurchaseOrderSource;
  /** Sorgu dizesinden (`?id=…`, `?id=…&tab=receipts`; `purchaseOrderRoute`). */
  route: PurchaseOrderRoute;
  /** Kartta gösterilecek bildirim (ör. "Sipariş açıldı"). */
  message?: string | null;
}

/**
 * Purchase Orders (M12c Task 16; `gemi-satin-alma.jpeg` 3 ve 11, `satin-alma-akisi.jpeg`
 * "Purchase Order"): liste ve sipariş kartı. Ofiste yazılır; gemide salt okunur, karttan mal
 * kabulü formu açılır (ADR 0026 §1; ADR 0040).
 */
export function PurchaseOrdersScreen({ source, route, message = null }: PurchaseOrdersScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const [notice, setNotice] = useState<string | null>(message);
  const [creating, setCreating] = useState(false);
  const go = (next: PurchaseOrderRoute) => {
    setNotice(null);
    shell?.navigate(purchaseOrderPath(next));
  };
  const ship = source.scope === 'ship';
  const editor = source.editor;

  if (route.view === 'detail') {
    return (
      <PurchaseOrderDetailView
        key={route.id}
        source={source}
        id={route.id}
        tab={route.tab}
        message={notice}
        onTab={(tab) => shell?.navigate(purchaseOrderPath({ view: 'detail', id: route.id, tab }))}
        onBack={() => go({ view: 'list' })}
        onOpenRequisition={(id) => shell?.navigate(requisitionPath({ view: 'detail', id }))}
        onOpenReceipt={(id) => shell?.navigate(grnPath({ view: 'detail', id }))}
        // RFQ yalnız ofiste; mal kabulü yalnız gemide (ADR 0026 §1)
        {...(ship
          ? { onReceive: (id: string) => shell?.navigate(grnPath({ view: 'new', orderId: id })) }
          : {
              onOpenRfq: (id: string) =>
                shell?.navigate(rfqPath({ view: 'detail', id, tab: 'details' })),
            })}
      />
    );
  }
  return (
    <>
      <PurchaseOrderList
        source={source}
        onOpen={(id) => go({ view: 'detail', id, tab: 'items' })}
        onNew={() => setCreating(true)}
      />
      {creating && editor ? (
        <NewPurchaseOrderDialog
          editor={editor}
          onClose={() => setCreating(false)}
          onCreated={(order) => {
            setCreating(false);
            setNotice(t('purchasing.po.detail.notices.created', { no: order.poNo }));
            shell?.navigate(purchaseOrderPath({ view: 'detail', id: order.id, tab: 'items' }));
          }}
        />
      ) : null}
    </>
  );
}

export { PURCHASE_ORDERS_PATH };
